import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Blogs from "./Blog";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    companies: 0,
    diagnostics: 0,
    appointments: 0,
    bookings: 0,
    staff: 0,
    doctors: 0,
    hra: 0,
  });

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        "https://api.elthiumhealth.com/api/admin/getdashboardcount",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        const { counts, chart } = response.data.data;
        setDashboardData(counts || {});
        setChartData(chart || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  fetchDashboardData();
}, []);

  // Navigation handlers
  const handleCompanyClick = () => navigate("/companylist");
  const handleDiagnosticsClick = () => navigate("/diagnosticlist");
  const handleAppointmentsClick = () => navigate("/appintmentlist");
  const handleHRAClick = () => navigate("/hrasubmissionlist");
  const handleUsersClick = () => navigate("/dashboard");
  const handleDoctorsClick = () => navigate("/doctorlist");
  const handleDiagnosticBookingClick = () => navigate("/diagnosticslist");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center md:text-left">
          <h1 className="text-4xl font-bold text-gray-800 tracking-tight">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Overview of your platform</p>
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard title="Companies" value={dashboardData.companies} onClick={handleCompanyClick} />
          <StatCard title="Diagnostics" value={dashboardData.diagnostics} onClick={handleDiagnosticsClick} />
          <StatCard title="Doctor Appointments" value={dashboardData.appointments} onClick={handleAppointmentsClick} />
          <StatCard title="HRA Submissions" value={dashboardData.hra} onClick={handleHRAClick} />
          <StatCard title="Users" value={dashboardData.staff} onClick={handleUsersClick} />
          <StatCard title="Doctors" value={dashboardData.doctors} onClick={handleDoctorsClick} />
          <StatCard title="Diagnostic Bookings" value={dashboardData.bookings} onClick={handleDiagnosticBookingClick} />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <ChartCard
            title="Doctor Appointments Trend"
            data={chartData}
            dataKey="doctor"
            barColor="#10B981"
            legend="Appointments"
            gradient="from-emerald-500 to-teal-600"
          />
          <ChartCard
            title="Diagnostic Bookings Trend"
            data={chartData}
            dataKey="diagnostic"
            barColor="#3B82F6"
            legend="Bookings"
            gradient="from-blue-500 to-indigo-600"
          />
        </div>

        {/* Blogs Section */}
        <div className="mt-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <span className="text-2xl">📰</span> Latest Blogs & Updates
              </h3>
            </div>
            <div className="p-6">
              <Blogs />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Stat Card with attractive hover effect and border accent
const StatCard = ({ title, value, onClick }) => (
  <div
    onClick={onClick}
    className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-blue-200"
  >
    {/* Top colored accent bar */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
    
    <div className="p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">{title}</span>
        <span className="text-gray-300 text-sm group-hover:translate-x-1 transition-transform duration-200">→</span>
      </div>
      <div className="text-4xl font-bold text-gray-800 mb-2">{value ?? 0}</div>
      <div className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        View details
      </div>
    </div>
  </div>
);

// Enhanced Chart Card with better styling
const ChartCard = ({ title, data, dataKey, barColor, legend, gradient }) => (
  <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl border border-gray-100">
    <div className={`bg-gradient-to-r ${gradient} px-6 py-4`}>
      <h3 className="text-xl font-semibold text-white tracking-tight">{title}</h3>
    </div>
    <div className="p-6">
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fill: '#4B5563', fontSize: 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#4B5563', fontSize: 12 }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={false}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
              padding: '10px 14px',
              fontSize: '13px'
            }}
            cursor={{ fill: '#F9FAFB' }}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '16px' }}
            iconType="circle"
          />
          <Bar 
            dataKey={dataKey} 
            fill={barColor} 
            name={legend}
            radius={[6, 6, 0, 0]}
            animationDuration={1200}
            animationEasing="ease-out"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default Dashboard;